import { AfterViewInit, Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

/**
 * Blocca la larghezza delle colonne sul contenuto della pagina visibile.
 * Il ricalcolo avviene solo quando cambia appStableColumns (caricamento dati o cambio pagina),
 * non quando i filtri modificano le righe.
 */
@Directive({
    selector: 'table[appStableColumns]'
})
export class StableColumnsDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input() appStableColumns = 0;

    private contentWidths: number[] | null = null;
    private flexIndex = -1;
    private frame = 0;
    private measuring = false;
    private resizeObserver: ResizeObserver | null = null;

    constructor(private host: ElementRef<HTMLTableElement>) {
    }

    ngAfterViewInit(): void {
        const parent = this.table.parentElement;
        if (parent) {
            this.resizeObserver = new ResizeObserver(() => {
                if (!this.measuring && this.contentWidths) {
                    this.applyWidths();
                }
            });
            this.resizeObserver.observe(parent);
        }
        this.scheduleMeasure();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['appStableColumns'] && !changes['appStableColumns'].firstChange) {
            this.scheduleMeasure();
        }
    }

    ngOnDestroy(): void {
        cancelAnimationFrame(this.frame);
        this.resizeObserver?.disconnect();
    }

    private get table(): HTMLTableElement {
        return this.host.nativeElement;
    }

    private scheduleMeasure(): void {
        this.measuring = true;
        this.unlock();
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
            this.frame = requestAnimationFrame(() => this.measure());
        });
    }

    private measure(): void {
        const table = this.table;
        if (!this.hasDataRows() || !table.parentElement?.clientWidth) {
            this.measuring = false;
            if (this.contentWidths) {
                this.applyWidths();
            }
            return;
        }

        table.style.tableLayout = 'auto';
        table.style.width = 'max-content';
        table.style.minWidth = '0';
        this.clearColWidths();
        void table.offsetWidth;

        const cells = this.titleCells();
        this.flexIndex = cells.findIndex(cell => cell.classList.contains('col-flex'));
        const idWidth = this.idColumnWidth();
        this.contentWidths = cells.map(cell => {
            if (cell.classList.contains('col-id')) {
                return idWidth;
            }
            return Math.ceil(cell.getBoundingClientRect().width);
        });
        this.measuring = false;
        this.applyWidths();
    }

    private applyWidths(): void {
        const widths = this.contentWidths;
        if (!widths?.length) {
            return;
        }
        const table = this.table;
        const parentWidth = table.parentElement?.clientWidth ?? 0;
        const applied = widths.slice();
        const sum = applied.reduce((total, width) => total + width, 0);
        if (this.flexIndex >= 0 && parentWidth > sum) {
            applied[this.flexIndex] += parentWidth - sum;
        }

        this.ensureCols(applied.length);
        const cols = table.querySelectorAll(':scope > colgroup > col');
        applied.forEach((width, index) => {
            (cols[index] as HTMLElement).style.width = `${width}px`;
        });

        table.classList.add('table-locked');
        table.style.tableLayout = 'fixed';
        table.style.minWidth = '100%';
        table.style.width = `${Math.max(sum, parentWidth)}px`;
    }

    private unlock(): void {
        const table = this.table;
        table.classList.remove('table-locked');
        table.style.tableLayout = '';
        table.style.width = '';
        table.style.minWidth = '';
        this.clearColWidths();
    }

    private clearColWidths(): void {
        this.table.querySelectorAll(':scope > colgroup > col').forEach(col => {
            (col as HTMLElement).style.width = '';
        });
    }

    private ensureCols(count: number): void {
        const table = this.table;
        let group = table.querySelector(':scope > colgroup');
        if (!group) {
            group = document.createElement('colgroup');
            table.insertBefore(group, table.firstChild);
        }
        while (group.children.length < count) {
            group.appendChild(document.createElement('col'));
        }
        while (group.children.length > count) {
            group.removeChild(group.lastElementChild as Node);
        }
    }

    private titleCells(): HTMLElement[] {
        const row = this.table.querySelector('tr.column-titles');
        return Array.from(row?.children ?? []) as HTMLElement[];
    }

    private hasDataRows(): boolean {
        return Array.from(this.table.tBodies[0]?.rows ?? []).some(row => !row.querySelector('.filtered-empty-state'));
    }

    private idColumnWidth(): number {
        const header = this.table.querySelector('tr.column-titles th.col-id') as HTMLElement | null;
        if (!header) {
            return 0;
        }

        const styles = getComputedStyle(header);
        const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const probe = document.createElement('span');
        probe.style.cssText = [
            'position:absolute',
            'visibility:hidden',
            'white-space:nowrap',
            `font:${styles.font}`,
            `letter-spacing:${styles.letterSpacing}`,
            `text-transform:${styles.textTransform}`
        ].join(';');
        probe.textContent = header.innerText.replace(/\s+/g, ' ').trim();
        document.body.appendChild(probe);
        const width = Math.ceil(probe.getBoundingClientRect().width) + padding;
        probe.remove();
        return width;
    }
}
